import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../../../firebase';
import './reports.css';

// SeasonalPayments read/write is done via admin-only Cloud Functions

const READY_FOR_PICKUP_STATUS = 'ready for pick up';

function isReadyForPickupStatus(status) {
  return (status || '').toLowerCase().trim() === READY_FOR_PICKUP_STATUS;
}

/**
 * Date/time of the first statusLog entry marked "ready for pick up" whose timestamp falls in [startDate, endDate].
 * Reports and per-day owed use only this day — not "picked up" or other statuses.
 */
function getOrderReadyForPickupDateInRange(order, startDate, endDate) {
  const log = order.statusLog;
  if (!Array.isArray(log) || log.length === 0) return null;
  const dates = log
    .filter((entry) => {
      if (!isReadyForPickupStatus(entry.status)) return false;
      const date = getStatusLogEntryDate(entry);
      return date && date >= startDate && date <= endDate;
    })
    .map((entry) => getStatusLogEntryDate(entry))
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());
  return dates.length > 0 ? dates[0] : null;
}

function orderHasReadyForPickupInRange(order, startDate, endDate) {
  return getOrderReadyForPickupDateInRange(order, startDate, endDate) != null;
}

/** Calendar day (YYYY-MM-DD local) when this order counts for the report window — first "ready for pick up" in range. */
function getOrderPaymentDayStrInRange(order, startDate, endDate) {
  const d = getOrderReadyForPickupDateInRange(order, startDate, endDate);
  return d ? toDateStrLocal(d) : null;
}

/** Calendar day for crediting this alteration: always the "ready for pick up" log timestamp (when in range). */
function getAlterationCompletionDate(order, item, alterationKey, startDate, endDate) {
  return getOrderReadyForPickupDateInRange(order, startDate, endDate);
}

// All alteration keys stored in order.line_items[].alterationDetails (must match admin/dashboard)
const ALTERATION_TYPES = [
  // Jacket
  'sleeveLonger',
  'sleeveShorter',
  'takeIn',
  'takeOut',
  'shorterJacket',
  'shoulderPads',
  'functionButtonHole',
  'changeButtonsJacket',
  'taperSleeve',
  'shattnezTest',
  'removeShattnez',
  // Pants
  'hemming',
  'taperPants',
  'waistIn',
  'waistOut',
  'seatIn',
  'seatOut',
  'crotchIn',
  'crotchOut',
  'crotchLining',
  'changeZipper',
  'length',
  'changeButtons',
  'suspenderButtons',
  // Shirt
  'shorterSleeve',
  'takeInShirt',
  'takeOutShirt',
  'iron',
  // Vest
  'changeButtonsVest',
  'takeInVest',
  'takeOutVest'
];

function toDateStrLocal(date) {
  if (!(date instanceof Date)) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Sort alteration type keys by count descending, then key name for stable ties. */
function sortAlterationTypesByCountDesc(types, countByType) {
  return [...types].sort((a, b) => {
    const ca = countByType[a] || 0;
    const cb = countByType[b] || 0;
    if (cb !== ca) return cb - ca;
    return a.localeCompare(b);
  });
}

/** Sort [alterationKey, count] pairs by count descending, then key. */
function sortAlterationEntriesByCountDesc(entries) {
  return [...entries].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return a[0].localeCompare(b[0]);
  });
}

function listDateStrsInRangeInclusive(start, end) {
  if (!(start instanceof Date) || !(end instanceof Date)) return [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const last = new Date(end);
  last.setHours(23, 59, 59, 999);
  const out = [];
  while (cursor <= last) {
    out.push(toDateStrLocal(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/** Get Date from a statusLog entry timestamp (Firestore Timestamp or { seconds } or ISO string). */
function getStatusLogEntryDate(entry) {
  if (!entry?.timestamp) return null;
  const t = entry.timestamp;
  if (t && typeof t.toDate === 'function') return t.toDate();
  if (typeof t === 'object' && typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}

/** Chronological list of status changes from order.statusLog (new status + when). */
function buildStatusHistoryFromOrder(order) {
  const log = order?.statusLog;
  if (!Array.isArray(log) || log.length === 0) return [];
  return log
    .map((entry) => ({
      at: getStatusLogEntryDate(entry),
      status: (entry?.status || '').trim() || '—',
      userName: (entry?.userName || '').trim()
    }))
    .filter((e) => e.at)
    .sort((a, b) => a.at.getTime() - b.at.getTime());
}

function formatStatusChangeDateTime(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York'
  });
}

function formatStatusForDisplay(status) {
  const s = (status || '').trim();
  if (!s || s === '—') return s || '—';
  return s.split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

/** Returns list of alteration keys that are set on the item (from ALTERATION_TYPES). */
function getItemAlterationKeys(item) {
  if (!item?.alterationDetails) return [];
  return ALTERATION_TYPES.filter((key) => {
    const v = item.alterationDetails[key];
    return v === true || (typeof v === 'string' && v.trim() !== '') || (typeof v === 'number' && !Number.isNaN(v));
  });
}

/**
 * Resolve which user id gets credit for this alteration.
 * If line item has alterationAssignments[key], that user id gets it; else the order's global assignee (assignedUser) does.
 */
function resolveAssigneeUserId(order, item, alterationKey, usersData) {
  const assigned = item?.alterationAssignments?.[alterationKey];
  if (assigned) return assigned; // stored as userId
  const globalName = (order?.assignedUser || '').trim();
  if (!globalName) return null;
  const user = usersData.find((u) => u.id === globalName || (u.name || '').trim() === globalName);
  return user ? user.id : null;
}

/** True if this alteration was assigned via split (alterationAssignments) rather than global assignee. */
function isSplitAssignment(item, alterationKey) {
  return Boolean(item?.alterationAssignments?.[alterationKey]);
}

/** Get finished-at date for a split alteration (alterationFinished[key].finishedAt). */
function getAlterationFinishedAt(item, alterationKey) {
  const entry = item?.alterationFinished?.[alterationKey];
  if (!entry) return null;
  const t = entry.finishedAt;
  if (!t) return null;
  if (t && typeof t.toDate === 'function') return t.toDate();
  if (typeof t === 'object' && typeof t.seconds === 'number') return new Date(t.seconds * 1000);
  const d = new Date(t);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * True if this alteration counts within [startDate, endDate].
 * Order must have a statusLog "ready for pick up" entry in that range (day of that log drives totals).
 * Split assignments must also have alterationFinished[key] set (work marked done).
 */
function isAlterationCompletedInRange(order, item, alterationKey, startDate, endDate) {
  if (!orderHasReadyForPickupInRange(order, startDate, endDate)) return false;
  if (isSplitAssignment(item, alterationKey)) {
    return getAlterationFinishedAt(item, alterationKey) != null;
  }
  return true;
}

// Map order alterationDetails keys to user seasonal rate keys (profile) – for owed calculation
const ALTERATION_TO_RATE_KEY = {
  sleeveLonger: 'sleeveLonger',
  sleeveShorter: 'sleeveShorter',
  takeIn: 'takeInJacket',
  takeOut: 'takeOutJacket',
  shorterJacket: 'shorterJacket',
  shoulderPads: 'shoulderPads',
  functionButtonHole: 'functionButtonHole',
  changeButtonsJacket: 'changeButtonsJacket',
  taperSleeve: 'taperSleeve',
  shattnezTest: 'shattnezTest',
  removeShattnez: 'removeShattnez',
  hemming: 'hemming',
  taperPants: 'taperPants',
  waistIn: 'waistIn',
  waistOut: 'waistOut',
  seatIn: 'seatIn',
  seatOut: 'seatOut',
  crotchIn: 'crotchIn',
  crotchOut: 'crotchOut',
  crotchLining: 'crotchLining',
  changeZipper: 'changeZipper',
  length: 'lengthPants',
  changeButtons: 'changeButtonsPants',
  suspenderButtons: 'suspenderButtons',
  shorterSleeve: 'shorterSleeveShirt',
  takeInShirt: 'takeInShirt',
  takeOutShirt: 'takeOutShirt',
  iron: 'ironShirt',
  changeButtonsVest: 'changeButtonsVest',
  takeInVest: 'takeInVest',
  takeOutVest: 'takeOutVest'
};

/**
 * Per-day owed for a seasonal worker in [start, end]. Returns { days: [{ dateStr, owed }], totalOwed }.
 * Includes every day in the range (owed may be 0).
 */
function computeDayBreakdown(orders, users, userId, user, start, end) {
  const dayOwed = {};
  orders.forEach((order) => {
    if (!order.line_items || !Array.isArray(order.line_items)) return;
    order.line_items.forEach((item) => {
      if (!item.needsAlteration) return;
      const quantity = Math.max(1, parseInt(item.quantity, 10) || 1);
      const keys = getItemAlterationKeys(item);
      keys.forEach((alterationKey) => {
        const assigneeId = resolveAssigneeUserId(order, item, alterationKey, users);
        if (assigneeId !== userId) return;
        if (!isAlterationCompletedInRange(order, item, alterationKey, start, end)) return;
        const completionDate = getAlterationCompletionDate(order, item, alterationKey, start, end);
        if (!completionDate) return;
        const dateStr = toDateStrLocal(completionDate);
        let owed = 0;
        if (user.position === 'Seasonal' && user.seasonalRates) {
          const rateKey = ALTERATION_TO_RATE_KEY[alterationKey];
          const rate = rateKey != null && user.seasonalRates[rateKey] != null ? Number(user.seasonalRates[rateKey]) : 0;
          owed = quantity * rate;
        }
        dayOwed[dateStr] = (dayOwed[dateStr] || 0) + owed;
      });
    });
  });

  const allDateStrs = listDateStrsInRangeInclusive(start, end);
  const days = allDateStrs
    .map((dateStr) => ({ dateStr, owed: dayOwed[dateStr] || 0 }))
    .sort((a, b) => a.dateStr.localeCompare(b.dateStr));
  const totalOwed = days.reduce((sum, d) => sum + d.owed, 0);
  return { days, totalOwed };
}

/** Get start and end dates for report filter (matches orders by "ready for pick up" log timestamp in this range). */
function getCompletionDateRange(rangeKey, customStart = '', customEnd = '') {
  if (rangeKey === 'custom' && customStart && customEnd) {
    const [y1, m1, d1] = customStart.split('-').map(Number);
    const [y2, m2, d2] = customEnd.split('-').map(Number);
    const start = new Date(y1, m1 - 1, d1, 0, 0, 0, 0);
    const end = new Date(y2, m2 - 1, d2, 23, 59, 59, 999);
    return { start, end };
  }
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  let start;
  if (rangeKey === 'today') {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  } else if (rangeKey === 'week') {
    start = new Date(now);
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);
  } else {
    // month
    start = new Date(now);
    start.setDate(start.getDate() - 30);
    start.setHours(0, 0, 0, 0);
  }
  return { start, end };
}

const Reports = () => {
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completionDateRange, setCompletionDateRange] = useState('week'); // 'today' | 'week' | 'month' | 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customReportResults, setCustomReportResults] = useState([]);
  const [customReportLoading, setCustomReportLoading] = useState(false);
  const [customReportError, setCustomReportError] = useState('');
  const [customReportUserFilter, setCustomReportUserFilter] = useState('');
  const [reportPeriodStart, setReportPeriodStart] = useState(''); // YYYY-MM-DD
  const [reportPeriodEnd, setReportPeriodEnd] = useState('');
  const [paymentsByDay, setPaymentsByDay] = useState({}); // { [dateStr]: { paidAt, paidBy } }
  const [markingPaidDay, setMarkingPaidDay] = useState(''); // dateStr being marked, or ''
  const [ordersDetailExpanded, setOrdersDetailExpanded] = useState(false);
  /** When set, "Orders by receipt" only shows orders whose ready-for-pick-up day in the report range matches (seasonal week/month). */
  const [paymentDayOrderFilter, setPaymentDayOrderFilter] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const usersRef = collection(db, 'Users');
        const usersSnapshot = await getDocs(usersRef);
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);

        const ordersRef = collection(db, 'Orders');
        const ordersSnapshot = await getDocs(ordersRef);
        const ordersData = ordersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setOrders(ordersData);
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Collapse the "Orders by receipt" list when user/period changes.
  useEffect(() => {
    setOrdersDetailExpanded(false);
    setPaymentDayOrderFilter('');
  }, [customReportUserFilter, reportPeriodStart, reportPeriodEnd]);

  // Load per-day payment status when viewing a seasonal worker's report
  useEffect(() => {
    if (!customReportUserFilter || !reportPeriodStart || !reportPeriodEnd || !orders.length || !users.length) {
      setPaymentsByDay({});
      return;
    }
    const selectedResult = customReportResults.find((r) => r.user.id === customReportUserFilter);
    if (!selectedResult?.user || selectedResult.user.position !== 'Seasonal') {
      setPaymentsByDay({});
      return;
    }
    const start = new Date(reportPeriodStart + 'T00:00:00');
    const end = new Date(reportPeriodEnd + 'T23:59:59.999');
    const { days } = computeDayBreakdown(orders, users, customReportUserFilter, selectedResult.user, start, end);
    if (days.length === 0) {
      setPaymentsByDay({});
      return;
    }
    const load = async () => {
      const getPaymentsFn = httpsCallable(functions, "getSeasonalPaymentsByDates");
      const res = await getPaymentsFn({
        userId: customReportUserFilter,
        dates: days.map(({ dateStr }) => dateStr)
      });

      const byDate = res?.data?.byDate || {};
      const byDay = {};
      Object.entries(byDate).forEach(([dateStr, payment]) => {
        const paidAt = payment?.paidAtIso ? new Date(payment.paidAtIso) : null;
        byDay[dateStr] = { paidAt, paidBy: payment?.paidBy || "" };
      });

      setPaymentsByDay(byDay);
    };
    load();
  }, [customReportUserFilter, reportPeriodStart, reportPeriodEnd, customReportResults, orders, users]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // const formatDate = (timestamp) => {
  //   if (!timestamp) return 'N/A';
  //   const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  //   return date.toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: 'short',
  //     day: 'numeric'
  //   });
  // };

  const formatAlterationLabel = (label) => {
    if (!label) return '';
    return label
      .replace(/([A-Z])/g, ' $1')
      .split(/[\s_]/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatPaymentDate = (date) => {
    if (!date || !(date instanceof Date)) return '';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const markPaymentPaid = async (dateStr) => {
    if (!customReportUserFilter || !dateStr) return;
    setMarkingPaidDay(dateStr);
    try {
      const markPaidFn = httpsCallable(functions, "markSeasonalPaymentPaid");
      await markPaidFn({
        userId: customReportUserFilter,
        dateStr
      });

      // Optimistically update UI (exact paidAt timestamp is written server-side)
      const paidBy = auth.currentUser?.email || auth.currentUser?.displayName || 'Admin';
      setPaymentsByDay((prev) => ({
        ...prev,
        [dateStr]: { paidAt: new Date(), paidBy }
      }));
    } catch (err) {
      console.error('Error marking payment paid:', err);
    } finally {
      setMarkingPaidDay('');
    }
  };

  const generateCustomReport = () => {
    setCustomReportError('');
    setCustomReportResults([]);
    if (completionDateRange === 'custom') {
      if (!customStartDate || !customEndDate) {
        setCustomReportError('Please select both start and end dates.');
        return;
      }
      const start = new Date(customStartDate + 'T00:00:00');
      const end = new Date(customEndDate + 'T23:59:59.999');
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        setCustomReportError('Invalid date selection.');
        return;
      }
      if (start > end) {
        setCustomReportError('Start date must be before end date.');
        return;
      }
    }
    const { start, end } = getCompletionDateRange(completionDateRange, customStartDate, customEndDate);
    setCustomReportLoading(true);

    // Per-user aggregates: only count alterations assigned to each user (split or global assignee)
    const alterationBreakdownByUser = {};
    const splitBreakdownByUser = {};
    const totalAlterationsByUser = {};
    const orderIdsByUser = {};
    const orderDetailsByUser = {}; // userId -> { orderId -> { receiptNumber, alterations: [key, ...] } }

    users.forEach((u) => {
      alterationBreakdownByUser[u.id] = ALTERATION_TYPES.reduce((acc, type) => {
        acc[type] = 0;
        return acc;
      }, {});
      splitBreakdownByUser[u.id] = ALTERATION_TYPES.reduce((acc, type) => {
        acc[type] = 0;
        return acc;
      }, {});
      totalAlterationsByUser[u.id] = 0;
      orderIdsByUser[u.id] = new Set();
      orderDetailsByUser[u.id] = {};
    });

    orders.forEach((order) => {
      if (!order.line_items || !Array.isArray(order.line_items)) return;
      const receiptLabel = (order.receiptNumber && String(order.receiptNumber).trim()) || order.id || '';
      order.line_items.forEach((item) => {
        if (!item.needsAlteration) return;
        const quantity = Math.max(1, parseInt(item.quantity, 10) || 1);
        const keys = getItemAlterationKeys(item);
        keys.forEach((alterationKey) => {
          const userId = resolveAssigneeUserId(order, item, alterationKey, users);
          if (!userId || !alterationBreakdownByUser[userId]) return;
          if (!isAlterationCompletedInRange(order, item, alterationKey, start, end)) return;
          alterationBreakdownByUser[userId][alterationKey] = (alterationBreakdownByUser[userId][alterationKey] || 0) + quantity;
          if (isSplitAssignment(item, alterationKey)) {
            splitBreakdownByUser[userId][alterationKey] = (splitBreakdownByUser[userId][alterationKey] || 0) + quantity;
          }
          totalAlterationsByUser[userId] += quantity;
          orderIdsByUser[userId].add(order.id);
          if (!orderDetailsByUser[userId][order.id]) {
            orderDetailsByUser[userId][order.id] = {
              receiptNumber: receiptLabel,
              customerName: order.name || '',
              alterations: [],
              statusHistory: buildStatusHistoryFromOrder(order)
            };
          }
          for (let q = 0; q < quantity; q++) {
            orderDetailsByUser[userId][order.id].alterations.push(alterationKey);
          }
        });
      });
    });

    const results = users
      .map((user) => {
        const alterationBreakdown = alterationBreakdownByUser[user.id] || {};
        const splitBreakdown = splitBreakdownByUser[user.id] || {};
        const totalAlterations = totalAlterationsByUser[user.id] || 0;
        const ordersTaken = orderIdsByUser[user.id] ? orderIdsByUser[user.id].size : 0;
        const orderDetailsMap = orderDetailsByUser[user.id] || {};
        const orderDetails = Object.entries(orderDetailsMap).map(([orderId, data]) => {
          const alterationCounts = data.alterations.reduce((acc, key) => {
            acc[key] = (acc[key] || 0) + 1;
            return acc;
          }, {});
          let owed = 0;
          if (user.position === 'Seasonal' && user.seasonalRates) {
            Object.entries(alterationCounts).forEach(([type, count]) => {
              const rateKey = ALTERATION_TO_RATE_KEY[type];
              const rate = rateKey != null && user.seasonalRates[rateKey] != null ? Number(user.seasonalRates[rateKey]) : 0;
              owed += count * rate;
            });
          }
          return {
            orderId,
            receiptNumber: data.receiptNumber,
            customerName: data.customerName || '',
            alterations: data.alterations,
            owed,
            statusHistory: data.statusHistory || []
          };
        });
        let totalOwed = 0;
        if (user.position === 'Seasonal' && user.seasonalRates) {
          const rates = user.seasonalRates;
          Object.keys(alterationBreakdown).forEach((type) => {
            const count = alterationBreakdown[type];
            if (!count) return;
            const rateKey = ALTERATION_TO_RATE_KEY[type];
            const rate = rateKey != null && rates[rateKey] != null ? Number(rates[rateKey]) : 0;
            totalOwed += count * rate;
          });
        }
        return {
          user,
          ordersTaken,
          totalAlterations,
          alterationBreakdown,
          splitBreakdown,
          totalOwed,
          orderDetails
        };
      })
      .filter((result) => result.ordersTaken > 0 || result.totalAlterations > 0);

    setCustomReportResults(results);
    const startStr = toDateStrLocal(start);
    const endStr = toDateStrLocal(end);
    setReportPeriodStart(startStr);
    setReportPeriodEnd(endStr);
    setPaymentsByDay({});
    if (results.length === 0) {
      setCustomReportError('No activity found within the selected date range.');
    }
    setCustomReportLoading(false);
  };

  if (loading) {
    return (
      <div className="reports-container">
        <div className="loading">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h1>Alterations by Date Range</h1>
        <div className="reports-controls">
          <div className="completion-date-range-field">
            <label htmlFor="completionDateRange" title="Includes work only when the order was marked &quot;ready for pick up&quot; on a day in this range. &quot;Picked up&quot; does not count.">
              Ready for pick up date
            </label>
            <select
              id="completionDateRange"
              value={completionDateRange}
              onChange={(e) => setCompletionDateRange(e.target.value)}
              className="completion-date-range-select"
              title="Filter by the day the order was set to Ready for pick up (status log)."
            >
              <option value="today">Today</option>
              <option value="week">1 week</option>
              <option value="month">1 month</option>
              <option value="custom">Custom dates</option>
            </select>
          </div>
          {completionDateRange === 'custom' && (
            <>
              <div className="date-field">
                <label htmlFor="customStartDate">Start date</label>
                <input
                  id="customStartDate"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="date-field">
                <label htmlFor="customEndDate">End date</label>
                <input
                  id="customEndDate"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
            </>
          )}
          <div className="date-field">
            <label htmlFor="customReportUser">User</label>
            <select
              id="customReportUser"
              value={customReportUserFilter}
              onChange={(e) => setCustomReportUserFilter(e.target.value)}
              className="custom-report-user-select"
            >
              <option value="">All users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.name || user.email || user.id}</option>
              ))}
            </select>
          </div>
          <button className="generate-report-btn" onClick={generateCustomReport} disabled={customReportLoading}>
            {customReportLoading ? 'Generating…' : 'Generate Report'}
          </button>
        </div>
      </div>

      <div className="custom-report-section">
        {customReportError && (
          <p className="custom-report-error">{customReportError}</p>
        )}
        {customReportLoading ? (
          <p className="custom-report-loading">Generating report...</p>
        ) : customReportResults.length > 0 ? (
          <div className="custom-report-table-container">
            <table className="custom-report-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Orders Taken</th>
                  <th>Total Alterations</th>
                  <th>Owed</th>
                  <th>Alteration Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {customReportResults
                  .filter(result => !customReportUserFilter || result.user.id === customReportUserFilter)
                  .map(result => (
                  <tr key={result.user.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-name">{result.user.name}</div>
                      </div>
                    </td>
                    <td>{result.ordersTaken}</td>
                    <td>{result.totalAlterations}</td>
                    <td>
                      {result.user.position === 'Seasonal' && result.totalOwed != null
                        ? formatCurrency(result.totalOwed)
                        : '—'}
                    </td>
                    <td>
                      <div className="breakdown-chips">
                        {sortAlterationTypesByCountDesc(
                          ALTERATION_TYPES.filter((type) => result.alterationBreakdown[type] > 0),
                          result.alterationBreakdown
                        ).map((type) => {
                          const count = result.alterationBreakdown[type];
                          return (
                            <span className="breakdown-chip" key={`${result.user.id}-${type}`}>
                              {formatAlterationLabel(type)}: {count}
                            </span>
                          );
                        })}
                        {ALTERATION_TYPES.every(type => !result.alterationBreakdown[type]) && (
                          <span className="breakdown-chip empty">No alterations recorded</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          customReportError === '' && <p className="custom-report-placeholder">Choose the ready-for-pick-up date range and click Generate Report.</p>
        )}
        {customReportUserFilter && customReportResults.length > 0 && (() => {
          const selectedResult = customReportResults.find((r) => r.user.id === customReportUserFilter);
          if (!selectedResult?.orderDetails?.length) return null;
          const isSeasonal = selectedResult.user.position === 'Seasonal';
          let dayBreakdown = null;
          let reportRangeStart = null;
          let reportRangeEnd = null;
          if (isSeasonal && reportPeriodStart && reportPeriodEnd) {
            reportRangeStart = new Date(`${reportPeriodStart}T00:00:00`);
            reportRangeEnd = new Date(`${reportPeriodEnd}T23:59:59.999`);
            dayBreakdown = computeDayBreakdown(
              orders,
              users,
              customReportUserFilter,
              selectedResult.user,
              reportRangeStart,
              reportRangeEnd
            );
          }
          const owedDays =
            dayBreakdown && dayBreakdown.days.length > 0
              ? dayBreakdown.days.filter((d) => Number(d.owed) > 0)
              : [];
          const periodTotalOwed = dayBreakdown?.totalOwed ?? 0;
          const periodTotalUnpaid = owedDays.reduce((sum, { dateStr, owed }) => {
            if (paymentsByDay[dateStr]) return sum;
            return sum + Number(owed);
          }, 0);
          const periodTotalPaid = periodTotalOwed - periodTotalUnpaid;
          const paidDaysCount = owedDays.filter((d) => paymentsByDay[d.dateStr]).length;
          const filteredOrderDetails =
            reportRangeStart && reportRangeEnd && paymentDayOrderFilter
              ? selectedResult.orderDetails.filter(({ orderId }) => {
                  const order = orders.find((o) => o.id === orderId);
                  if (!order) return false;
                  return getOrderPaymentDayStrInRange(order, reportRangeStart, reportRangeEnd) === paymentDayOrderFilter;
                })
              : selectedResult.orderDetails;
          return (
            <div className="user-orders-detail-section">
              <h3 className="user-orders-detail-title">Orders by receipt — {selectedResult.user.name}</h3>
              {isSeasonal && dayBreakdown && dayBreakdown.days.length > 0 && (
                <div className="seasonal-payment-by-day">
                  <h4 className="payment-by-day-title">Payment by day</h4>
                  {owedDays.length > 0 && (
                    <div className="payment-by-day-summary">
                      <div className="payment-by-day-summary-item">
                        <span className="payment-by-day-summary-label">Total for selected period</span>
                        <span className="payment-by-day-summary-value">{formatCurrency(periodTotalOwed)}</span>
                      </div>
                      <div className="payment-by-day-summary-item payment-by-day-summary-unpaid">
                        <span className="payment-by-day-summary-label">Total unpaid</span>
                        <span className="payment-by-day-summary-value">{formatCurrency(periodTotalUnpaid)}</span>
                      </div>
                      <div className="payment-by-day-summary-item payment-by-day-summary-paid">
                        <span className="payment-by-day-summary-label">Total paid</span>
                        <span className="payment-by-day-summary-value">{formatCurrency(periodTotalPaid)}</span>
                      </div>
                    </div>
                  )}
                  {owedDays.length === 0 ? null : (
                    <table className="payment-by-day-table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Owed</th>
                          <th>Status</th>
                          <th>Orders that day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {owedDays.map(({ dateStr, owed }) => {
                          const paid = paymentsByDay[dateStr];
                          const displayDate = new Date(`${dateStr}T12:00:00`).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                          const rowActive = paymentDayOrderFilter === dateStr;
                          return (
                            <tr key={dateStr} className={rowActive ? 'payment-by-day-row-active' : undefined}>
                              <td>{displayDate}</td>
                              <td>{formatCurrency(owed)}</td>
                              <td>
                                {paid ? (
                                  <span className="seasonal-payment-paid">
                                    Paid on {formatPaymentDate(paid.paidAt)}
                                    {paid.paidBy ? ` by ${paid.paidBy}` : ''}
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    className="mark-payment-paid-btn"
                                    onClick={() => markPaymentPaid(dateStr)}
                                    disabled={markingPaidDay === dateStr}
                                  >
                                    {markingPaidDay === dateStr ? 'Saving…' : 'Mark as paid'}
                                  </button>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="payment-day-view-orders-btn"
                                  onClick={() => {
                                    setPaymentDayOrderFilter(dateStr);
                                    setOrdersDetailExpanded(true);
                                  }}
                                >
                                  {rowActive ? 'Showing in list' : 'View orders'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="payment-by-day-total">
                          <td>Total</td>
                          <td>{formatCurrency(periodTotalOwed)}</td>
                          <td colSpan={2}>
                            {paidDaysCount} of {owedDays.length} days paid
                            {periodTotalUnpaid > 0 && (
                              <span className="payment-by-day-total-unpaid">
                                {' '}· {formatCurrency(periodTotalUnpaid)} unpaid
                              </span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
              {paymentDayOrderFilter && (
                <div className="payment-day-filter-banner">
                  <span>
                    Showing receipts credited to{' '}
                    <strong>
                      {new Date(`${paymentDayOrderFilter}T12:00:00`).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </strong>{' '}
                    ({filteredOrderDetails.length}{' '}
                    {filteredOrderDetails.length === 1 ? 'receipt' : 'receipts'})
                  </span>
                  <button
                    type="button"
                    className="payment-day-filter-clear-btn"
                    onClick={() => setPaymentDayOrderFilter('')}
                  >
                    Show all days
                  </button>
                </div>
              )}
              <button
                type="button"
                className="user-orders-detail-toggle"
                onClick={() => setOrdersDetailExpanded((prev) => !prev)}
                aria-expanded={ordersDetailExpanded}
              >
                <span>{ordersDetailExpanded ? 'Hide orders' : 'Show orders'}</span>
                <span className="user-orders-detail-toggle-meta">
                  {paymentDayOrderFilter
                    ? `${filteredOrderDetails.length} of ${selectedResult.orderDetails.length} receipts`
                    : `${selectedResult.orderDetails.length} receipts`}
                </span>
                <span className="user-orders-detail-toggle-icon" aria-hidden>
                  {ordersDetailExpanded ? '▼' : '▶'}
                </span>
              </button>
              {ordersDetailExpanded && (
                <div className="user-orders-detail-list">
                  {filteredOrderDetails.length === 0 ? (
                    <p className="user-orders-detail-empty-filter">
                      No receipts match this day filter for the selected range.
                    </p>
                  ) : (
                  filteredOrderDetails.map(({ orderId, receiptNumber, customerName, alterations, owed, statusHistory }) => (
                    <div key={orderId} className="user-order-detail-row">
                      <div className="user-order-detail-row-main">
                        <span className="user-order-receipt">Receipt: {receiptNumber || orderId}</span>
                        <span className="user-order-customer">{customerName || '—'}</span>
                        <span className="user-order-alterations">
                          {sortAlterationEntriesByCountDesc(
                            Object.entries(
                              alterations.reduce((acc, key) => {
                                acc[key] = (acc[key] || 0) + 1;
                                return acc;
                              }, {})
                            )
                          )
                            .map(([key, count]) =>
                              count > 1 ? `${formatAlterationLabel(key)} (${count})` : formatAlterationLabel(key)
                            )
                            .join(', ')}
                        </span>
                        <span className="user-order-owed">
                          {selectedResult.user.position === 'Seasonal' && owed != null ? formatCurrency(owed) : '—'}
                        </span>
                      </div>
                      <details className="user-order-status-details" aria-label="Order status history">
                        <summary className="user-order-status-summary">
                          Status changes ({statusHistory?.length ?? 0})
                        </summary>
                        {!statusHistory || statusHistory.length === 0 ? (
                          <p className="user-order-status-empty">No status log on this order</p>
                        ) : (
                          <ul className="user-order-status-log">
                            {statusHistory.map((entry, idx) => (
                              <li key={`${orderId}-status-${idx}`}>
                                <span className="user-order-status-time">{formatStatusChangeDateTime(entry.at)}</span>
                                <span className="user-order-status-arrow" aria-hidden>→</span>
                                <span className="user-order-status-to">{formatStatusForDisplay(entry.status)}</span>
                                {entry.userName ? (
                                  <span className="user-order-status-by">({entry.userName})</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </details>
                    </div>
                  )))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Reports;
